'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const CARRIER_LIST = [
  { name: 'Chronopost', value: 'chronopost' },
  { name: 'Colissimo', value: 'colissimo' },
  { name: 'DHL', value: 'dhl' },
  { name: 'DHL Allemagne', value: 'dhl_de' },
  { name: 'DPD', value: 'dpd' },
  { name: 'DPD France', value: 'dpd_fr' },
  { name: 'DPD Royaume-Uni', value: 'dpd_gb' },
  { name: 'DPD (local)', value: 'dpd_local' },
  { name: 'GLS Allemagne', value: 'gls_de' },
  { name: 'GLS Espagne', value: 'gls_es' },
  { name: 'GLS Pays-Bas', value: 'gls_nl' },
  { name: 'Mondial Relay', value: 'mondial_relay' },
  { name: 'UPS', value: 'ups' },
]

const sendCloudFormSchema = z.object({
  apiToken: z.string().min(1, 'Public API Token is required'),
  carriers: z
    .array(
      z.object({
        value: z.string(),
        name: z.string(),
      }),
    )
    .min(1, 'At least one carrier must be selected'),
})

type FormValues = z.infer<typeof sendCloudFormSchema>

interface SendCloudFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function SendCloudForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: SendCloudFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(sendCloudFormSchema),
    defaultValues: {
      apiToken: defaultValues.credentials?.apiToken ?? '',
      carriers: defaultValues.credentials?.carriers ?? [],
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const credentials = data
    await onSubmit({ credentials })
  }

  const selectedCarriers = watch('carriers')

  const handleCarrierToggle = (carrier: { name: string; value: string }, checked: boolean) => {
    const currentCarriers = selectedCarriers || []
    if (checked) {
      setValue('carriers', [...currentCarriers, carrier], { shouldValidate: true })
    } else {
      setValue(
        'carriers',
        currentCarriers.filter((c) => c.value !== carrier.value),
        { shouldValidate: true }
      )
    }
  }

  const isCarrierSelected = (carrierValue: string) => {
    return selectedCarriers?.some((c) => c.value === carrierValue) ?? false
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('sendcloud.apiCredentials')}</CardTitle>
          <CardDescription>{t('sendcloud.apiCredentialsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiToken">
              {t('sendcloud.publicApiToken')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="apiToken"
              placeholder={t('sendcloud.apiTokenPlaceholder')}
              disabled={isLoading}
              {...register('apiToken')}
            />
            {errors.apiToken && (
              <p className="text-xs text-destructive">{errors.apiToken.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Carrier Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('sendcloud.carrierSelection')}</CardTitle>
          <CardDescription>
            {t('sendcloud.carrierSelectionDescription')}
            <span className="ml-1 text-destructive">*</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {CARRIER_LIST.map((carrier) => (
              <div key={carrier.value} className="flex items-center space-x-2">
                <Checkbox
                  id={carrier.value}
                  checked={isCarrierSelected(carrier.value)}
                  onCheckedChange={(checked) => handleCarrierToggle(carrier, checked as boolean)}
                  disabled={isLoading}
                />
                <Label htmlFor={carrier.value} className="cursor-pointer font-normal">
                  {carrier.name}
                </Label>
              </div>
            ))}
          </div>
          {errors.carriers && (
            <p className="text-xs text-destructive">{errors.carriers.message}</p>
          )}
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('sendcloud.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('sendcloud.setupStep1')}{' '}
                <a
                  href="https://panel.sendcloud.sc/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('sendcloud.setupStep1Link')}
                </a>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('sendcloud.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('sendcloud.setupStep3') }} />
              <li>{t('sendcloud.setupStep4')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('sendcloud.setupStep5') }} />
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
